import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { ArrowRight, ArrowUpRight, Instagram, MapPin, BedDouble, Bath, Ruler, Car, LandPlot } from "lucide-react";
import { API, IMAGES, SITE, PROCESS_STEPS, formatPrice, resolveImage, STATUS_COLORS } from "../data/site";
import { useContent, useSettings, useTestimonials } from "../hooks/useCms";
import { LineReveal, FadeUp, Stagger, StaggerItem } from "../components/motion";
import CyclingImage from "../components/CyclingImage";
import EditorialMarquee from "../components/EditorialMarquee";
import SEO from "../components/SEO";

const Hero = () => {
  const ref = useRef(null);
  const c = useContent();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#1A1A1A]">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt={IMAGES.heroAlt} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />
      </motion.div>

      <div className="relative z-10 h-full max-w-[1600px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="eyebrow text-[#F7F5F2]/70 mb-6"
        >
          {c("home_hero_eyebrow", "Idaho Custom Home Builder")}
        </motion.p>
        <h1 className="font-serif text-[#F7F5F2] text-[13vw] md:text-[9vw] lg:text-[7vw] leading-[0.95] tracking-tight">
          <LineReveal text={c("home_hero_line1", "Built With Intention.")} delay={0.15} />
          <LineReveal text={c("home_hero_line2", "Built For You.")} className="italic text-[#A3A09B]" delay={0.3} />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-[#F7F5F2]/85 text-base md:text-lg leading-relaxed"
        >
          {c("home_hero_sub", "On Point Homes takes a personal approach to building exceptional homes—thoughtfully designed and customized around the people who will call them home.")}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link
            to="/available-homes"
            data-testid="hero-view-homes-btn"
            className="btn-sweep bg-[#F7F5F2] text-[#1A1A1A] font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-8 py-4 flex items-center gap-3 transition-colors duration-300 hover:text-[#F7F5F2]"
          >
            View Available Homes <ArrowRight size={14} />
          </Link>
          <Link
            to="/custom-homes"
            data-testid="hero-build-with-us-btn"
            className="border border-[#F7F5F2]/50 text-[#F7F5F2] font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-8 py-4 flex items-center gap-3 hover:bg-[#F7F5F2] hover:text-[#1A1A1A] transition-colors duration-500"
          >
            Build With Us <ArrowUpRight size={14} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const Intro = () => {
  const c = useContent();
  return (
  <section data-testid="intro-section" className="py-24 md:py-36">
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
      <div className="lg:col-span-5">
        <FadeUp>
          <p className="eyebrow text-[#8C6A4F] mb-6">01 — Who We Are</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1A1A1A]">
            {c("home_intro_headline", "A builder who answers his own phone.")}
          </h2>
        </FadeUp>
      </div>
      <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-end">
        <FadeUp delay={0.15}>
          <p className="text-base md:text-lg leading-relaxed text-[#5C5A56]">
            {c("home_intro_p1", "We're not a production builder churning out hundreds of homes a year. On Point Homes is intentionally small — which means the person you shake hands with is the person walking your job site, reviewing your selections, and sweating the details you didn't even know to ask about.")}
          </p>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-[#5C5A56]">
            {c("home_intro_p2", "Your home should be uniquely yours. That's why every project receives our personal attention from start to finish.")}
          </p>
          <Link
            to="/about"
            data-testid="intro-about-link"
            className="mt-8 inline-flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold text-[#1A1A1A] link-underline w-fit"
          >
            More About Us <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </div>
    </div>
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 mt-16 md:mt-24 grid grid-cols-12 gap-4 md:gap-6">
      <FadeUp className="col-span-12 md:col-span-7 img-zoom relative aspect-[16/10]" delay={0.1}>
        <CyclingImage start={0} interval={5400} alt="Interior of a home built by On Point Homes" />
      </FadeUp>
      <FadeUp className="col-span-12 md:col-span-5 img-zoom relative aspect-[16/10] md:aspect-auto md:mt-16" delay={0.25}>
        <CyclingImage start={7} interval={5700} alt="Craftsmanship detail in an On Point Homes build" />
      </FadeUp>
    </div>
  </section>
  );
};

const FeaturedHome = () => {
  const { data } = useQuery({
    queryKey: ["featured-rotation"],
    queryFn: async () => (await axios.get(`${API}/homes?status=Available`)).data,
  });
  const homes = data || [];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (homes.length < 2) return;
    const t = setInterval(() => setIdx((p) => (p + 1) % homes.length), 6000);
    return () => clearInterval(t);
  }, [homes.length]);

  if (!homes.length) return null;
  const home = homes[idx % homes.length];
  const img = home.images?.length ? resolveImage(home.images[0]) : IMAGES.home1;

  return (
    <section data-testid="featured-home-section" className="py-24 md:py-32 bg-[#EFECE7]">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <FadeUp className="flex items-end justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="eyebrow text-[#8C6A4F] mb-6">02 — Now Available</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">Featured Homes</h2>
          </div>
          <div className="flex items-center gap-6">
            {homes.length > 1 && (
              <div className="flex gap-2" data-testid="featured-indicators">
                {homes.map((h, i) => (
                  <button
                    key={h.id}
                    onClick={() => setIdx(i)}
                    data-testid={`featured-dot-${i}`}
                    aria-label={`Show ${h.title}`}
                    className={`h-1 transition-[width,background-color] duration-500 ${i === idx % homes.length ? "w-10 bg-[#1A1A1A]" : "w-5 bg-[#1A1A1A]/25 hover:bg-[#1A1A1A]/50"}`}
                  />
                ))}
              </div>
            )}
            <Link to="/available-homes" data-testid="featured-view-all-link" className="hidden md:inline-flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold link-underline w-fit shrink-0">
              All Homes <ArrowRight size={14} />
            </Link>
          </div>
        </FadeUp>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-end">
          <FadeUp className="lg:col-span-7 img-zoom relative aspect-[4/3]" delay={0.1}>
            <AnimatePresence>
              <motion.img
                key={home.id}
                src={img}
                alt={`${home.title} — featured new construction home for sale in ${home.city}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1, ease: "easeInOut" }}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="featured-image"
              />
            </AnimatePresence>
          </FadeUp>
          <FadeUp className="lg:col-span-5" delay={0.2}>
            <AnimatePresence mode="wait">
              <motion.div
                key={home.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <span className={`inline-block font-mono text-[0.6rem] tracking-[0.2em] uppercase font-bold px-3 py-1.5 mb-6 ${STATUS_COLORS[home.status]}`}>
                  {home.status}
                </span>
                <h3 className="font-serif text-3xl md:text-5xl tracking-tight" data-testid="featured-title">{home.title}</h3>
                <p className="mt-2 font-mono text-lg font-semibold">{formatPrice(home.price)}</p>
                <p className="mt-1 text-sm text-[#5C5A56]">{home.address !== "Coming Soon" && `${home.address}, `}{home.city}</p>
                <div className="mt-8 grid grid-cols-2 gap-y-5 gap-x-8 border-t border-[rgba(26,26,26,0.12)] pt-8">
                  {[
                    { icon: BedDouble, label: `${home.beds} Bedrooms` },
                    { icon: Bath, label: `${home.baths} Bathrooms` },
                    { icon: Ruler, label: `${home.sqft?.toLocaleString()} Sq Ft` },
                    { icon: Car, label: home.garage },
                    { icon: LandPlot, label: home.lot },
                    { icon: MapPin, label: home.city },
                  ].filter(i => i.label && !i.label.includes("undefined")).map((item, i) => (
                    <span key={i} className="flex items-center gap-3 text-sm text-[#5C5A56]">
                      <item.icon size={16} className="text-[#8C6A4F] shrink-0" /> {item.label}
                    </span>
                  ))}
                </div>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to={`/available-homes/${home.id}`} data-testid="featured-details-btn" className="btn-sweep bg-[#1A1A1A] text-[#F7F5F2] font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-8 py-4 inline-flex items-center gap-3">
                    View This Home <ArrowRight size={14} />
                  </Link>
                  <Link to={`/contact?interest=Buying an Available Home&home=${encodeURIComponent(home.title)}`} data-testid="featured-tour-btn" className="border border-[#1A1A1A]/30 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-8 py-4 hover:bg-[#1A1A1A] hover:text-[#F7F5F2] transition-colors duration-500">
                    Schedule a Tour
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

const CustomPromo = () => {
  const c = useContent();
  return (
  <section data-testid="custom-promo-section" className="relative py-28 md:py-40 bg-[#1A1A1A] text-[#F7F5F2] overflow-hidden">
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-6 relative z-10">
        <FadeUp>
          <p className="eyebrow text-[#8C6A4F] mb-6">03 — Custom Homes</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-7xl leading-[1.02] tracking-tight">
            {c("home_custom_headline1", "Your land.")}<br />{c("home_custom_headline2", "Your vision.")}<br /><span className="italic text-[#A3A09B]">{c("home_custom_headline3", "Our craft.")}</span>
          </h2>
          <p className="mt-8 max-w-lg text-[#F7F5F2]/75 text-base md:text-lg leading-relaxed">
            {c("home_custom_sub", "Thoughtfully designed custom homes — built around the way you live, with personal attention at every step, honest communication, and craftsmanship you can see in every corner.")}
          </p>
          <Link to="/custom-homes" data-testid="custom-promo-btn" className="mt-10 inline-flex items-center gap-3 bg-[#F7F5F2] text-[#1A1A1A] font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-8 py-4 btn-sweep hover:text-[#F7F5F2] transition-colors duration-300">
            Explore Custom Homes <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </div>
      <FadeUp className="lg:col-span-5 lg:col-start-8 img-zoom relative aspect-[3/4] lg:mt-[-4rem]" delay={0.2}>
        <CyclingImage start={13} interval={5200} alt="Custom home built by On Point Homes in Idaho" />
      </FadeUp>
    </div>
    <span aria-hidden="true" className="pointer-events-none select-none absolute -bottom-10 left-0 font-serif text-[26vw] leading-none text-white/[0.03] whitespace-nowrap">
      Custom
    </span>
  </section>
  );
};

const PortfolioPreview = () => (
  <section data-testid="portfolio-preview-section" className="py-24 md:py-36">
    <div className="max-w-[1600px] mx-auto px-5 md:px-10">
      <FadeUp className="flex items-end justify-between gap-6 mb-14 md:mb-20">
        <div>
          <p className="eyebrow text-[#8C6A4F] mb-6">04 — Our Work</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-xl">
            Homes we're proud to put our name on.
          </h2>
        </div>
        <Link to="/our-work" data-testid="portfolio-view-all-link" className="hidden md:inline-flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold link-underline w-fit shrink-0">
          View Portfolio <ArrowRight size={14} />
        </Link>
      </FadeUp>
      <div className="grid grid-cols-12 gap-4 md:gap-6">
        <FadeUp className="col-span-12 md:col-span-5 img-zoom relative aspect-[3/4]" delay={0}>
          <CyclingImage start={18} interval={5600} alt="Completed home by On Point Homes" />
        </FadeUp>
        <FadeUp className="col-span-7 md:col-span-4 img-zoom relative aspect-[3/4] md:mt-24" delay={0.15}>
          <CyclingImage start={22} interval={5300} alt="Detail of an On Point Homes build in Idaho" />
        </FadeUp>
        <FadeUp className="col-span-5 md:col-span-3 img-zoom relative aspect-[3/4] md:mt-48" delay={0.3}>
          <CyclingImage start={26} interval={5900} alt="Interior craftsmanship by On Point Homes" />
        </FadeUp>
      </div>
      <Link to="/our-work" data-testid="portfolio-view-all-mobile" className="md:hidden mt-10 inline-flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold link-underline w-fit">
        View Portfolio <ArrowRight size={14} />
      </Link>
    </div>
  </section>
);

const WHY_POINTS = [
  { n: "01", title: "Personal, Not Production", text: "You work directly with your builder — not a sales office, not a handoff chain. One point of contact from first conversation to final walkthrough." },
  { n: "02", title: "Details Obsessed Over", text: "We sweat the reveals, the trim lines, the way light moves through a room. The things you notice years later are the things we plan for now." },
  { n: "03", title: "Honest Communication", text: "Clear budgets, realistic timelines, and straight answers. If something changes, you hear it from us first — not from a surprise invoice." },
  { n: "04", title: "Built For Idaho", text: "We build here because we live here. Homes designed for Idaho seasons, Idaho lots, and Idaho life — from the Treasure Valley outward." },
];

const WhyChoose = () => (
  <section data-testid="why-choose-section" className="py-24 md:py-32 border-t border-[rgba(26,26,26,0.1)]">
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <FadeUp>
          <p className="eyebrow text-[#8C6A4F] mb-6">05 — Why On Point</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">Why build with us</h2>
        </FadeUp>
      </div>
      <div className="lg:col-span-8">
        <Stagger>
          {WHY_POINTS.map((p) => (
            <StaggerItem key={p.n}>
              <div className="grid grid-cols-12 gap-4 py-8 border-b border-[rgba(26,26,26,0.12)] group">
                <span className="col-span-2 md:col-span-1 font-serif text-3xl md:text-4xl text-[#A3A09B] group-hover:text-[#8C6A4F] transition-colors duration-500">{p.n}</span>
                <div className="col-span-10 md:col-span-11">
                  <h3 className="font-serif text-2xl md:text-3xl tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-[#5C5A56] leading-relaxed max-w-2xl text-sm md:text-base">{p.text}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </div>
  </section>
);

const ProcessTeaser = () => (
  <section data-testid="process-teaser-section" className="py-24 md:py-32 bg-[#EFECE7]">
    <div className="max-w-[1600px] mx-auto px-5 md:px-10">
      <FadeUp className="flex items-end justify-between gap-6 mb-14">
        <div>
          <p className="eyebrow text-[#8C6A4F] mb-6">06 — The Process</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">Five steps. Zero mystery.</h2>
        </div>
        <Link to="/our-process" data-testid="process-view-link" className="hidden md:inline-flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold link-underline w-fit shrink-0">
          Full Process <ArrowRight size={14} />
        </Link>
      </FadeUp>
      <Stagger className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[rgba(26,26,26,0.1)]" gap={0.1}>
        {PROCESS_STEPS.map((s) => (
          <StaggerItem key={s.n} className="bg-[#F7F5F2] p-6 md:p-8 min-h-[180px] flex flex-col">
            <span className="font-serif text-4xl md:text-5xl text-[#8C6A4F]">{s.n}</span>
            <span className="mt-auto pt-6 font-mono text-[0.65rem] tracking-[0.2em] uppercase font-bold">{s.title}</span>
          </StaggerItem>
        ))}
      </Stagger>
      <Link to="/our-process" data-testid="process-view-mobile" className="md:hidden mt-10 inline-flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold link-underline w-fit">
        Full Process <ArrowRight size={14} />
      </Link>
    </div>
  </section>
);

const Testimonials = () => {
  const testimonials = useTestimonials();
  const sorted = [...testimonials].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  if (!sorted.length)
    return (
      <section data-testid="testimonials-section" className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <FadeUp>
              <p className="eyebrow text-[#8C6A4F] mb-6">07 — Client Stories</p>
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
                The best references are the homes themselves.
              </h2>
            </FadeUp>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <FadeUp delay={0.15}>
              <p className="font-serif italic text-2xl md:text-3xl text-[#A3A09B] leading-snug">
                "Client stories are on their way — our first homeowners are still unpacking. In the meantime,
                walk our work and judge for yourself."
              </p>
              <Link to="/our-work" data-testid="testimonials-work-link" className="mt-6 inline-flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold link-underline w-fit not-italic">
                See Our Work <ArrowRight size={14} />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>
    );

  return (
    <section data-testid="testimonials-section" className="py-24 md:py-32">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <FadeUp className="mb-14">
          <p className="eyebrow text-[#8C6A4F] mb-6">07 — Client Stories</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">What our homeowners say.</h2>
        </FadeUp>
        <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="testimonials-grid">
          {sorted.map((t) => (
            <StaggerItem key={t.id}>
              <figure className="h-full border-l-2 border-[#8C6A4F] bg-white/60 p-8 flex flex-col" data-testid={`testimonial-${t.id}`}>
                {t.rating > 0 && (
                  <div className="flex gap-1 mb-5" aria-label={`${t.rating} out of 5 stars`}>
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <span key={i} className="w-2 h-2 rotate-45 bg-[#8C6A4F]" />
                    ))}
                  </div>
                )}
                <blockquote className="font-serif italic text-xl md:text-2xl leading-snug text-[#1A1A1A] grow">
                  "{t.text}"
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-4">
                  {t.photo && <img src={resolveImage(t.photo)} alt={t.name} className="w-11 h-11 object-cover" loading="lazy" />}
                  <div>
                    <p className="font-mono text-[0.7rem] tracking-[0.18em] uppercase font-bold">{t.name}</p>
                    {(t.project || t.date) && (
                      <p className="text-xs text-[#A3A09B] mt-1">{[t.project, t.date].filter(Boolean).join(" — ")}</p>
                    )}
                  </div>
                </figcaption>
              </figure>
            </StaggerItem>
          ))}
        </Stagger>
        <FadeUp delay={0.2}>
          <Link to="/reviews" data-testid="testimonials-all-link" className="mt-10 inline-flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold link-underline w-fit">
            Read All Stories <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};

const InstagramSection = () => (
  <section data-testid="instagram-section" className="py-24 md:py-32 border-t border-[rgba(26,26,26,0.1)]">
    <div className="max-w-[1600px] mx-auto px-5 md:px-10">
      <FadeUp className="flex items-end justify-between gap-6 mb-12">
        <div>
          <p className="eyebrow text-[#8C6A4F] mb-6">08 — Follow Along</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">Behind the builds</h2>
        </div>
        <a href={SITE.instagram} target="_blank" rel="noreferrer" data-testid="instagram-link" className="inline-flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold link-underline w-fit shrink-0">
          <Instagram size={15} /> {SITE.instagramHandle}
        </a>
      </FadeUp>
      <a href={SITE.instagram} target="_blank" rel="noreferrer" className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 group" data-testid="instagram-grid">
        {[3, 9, 15, 24].map((start, i) => (
          <FadeUp key={i} delay={i * 0.08} className="img-zoom relative aspect-square">
            <CyclingImage start={start} interval={5100 + i * 300} alt="On Point Homes Instagram — Idaho custom home build" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
          </FadeUp>
        ))}
      </a>
    </div>
  </section>
);

const ServiceArea = () => {
  const c = useContent();
  const settings = useSettings();
  return (
  <section data-testid="service-area-section" className="relative py-28 md:py-40 overflow-hidden">
    <div className="absolute inset-0">
      <img src={IMAGES.idaho1} alt="Idaho mountain landscape — On Point Homes service area" className="w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/20" />
    </div>
    <div className="relative z-10 max-w-[1600px] mx-auto px-5 md:px-10">
      <FadeUp>
        <p className="eyebrow text-[#F7F5F2]/70 mb-6">09 — Where We Build</p>
        <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#F7F5F2] max-w-3xl">
          {c("areas_headline", "Proudly Building In Idaho")}
        </h2>
        <p className="mt-8 max-w-lg text-[#F7F5F2]/80 text-base md:text-lg leading-relaxed">
          <span className="block font-semibold text-[#F7F5F2]">Rooted in the Treasure Valley. Built for You.</span>
          <span className="block mt-4">{c("areas_text", "We build thoughtfully designed homes throughout the Treasure Valley, creating spaces that reflect the people who live in them.")}</span>
          <span className="block mt-4 font-semibold text-[#F7F5F2]">Personal attention. Thoughtful design. Quality craftsmanship.</span>
        </p>
        <p className="mt-6 flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.25em] uppercase font-bold text-[#F7F5F2]">
          <MapPin size={15} className="text-[#8C6A4F]" /> {settings.region || SITE.region}
        </p>
      </FadeUp>
    </div>
  </section>
  );
};

const FinalCTA = () => {
  const c = useContent();
  const settings = useSettings();
  const phone = settings.phone || SITE.phone;
  return (
  <section data-testid="final-cta-section" className="py-28 md:py-40 bg-[#F7F5F2]">
    <div className="max-w-[1600px] mx-auto px-5 md:px-10">
      <FadeUp>
        <p className="eyebrow text-[#8C6A4F] mb-8">Ready When You Are</p>
        <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight max-w-5xl">
          {c("home_cta_headline1", "Let's build something")}<br /><span className="italic text-[#8C6A4F]">{c("home_cta_headline2", "worth coming home to.")}</span>
        </h2>
        <div className="mt-12 flex flex-wrap gap-4">
          <Link to={settings.default_cta_link || "/contact"} data-testid="final-cta-btn" className="btn-sweep bg-[#1A1A1A] text-[#F7F5F2] font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-10 py-5 inline-flex items-center gap-3">
            {c("home_cta_button", "Start Your Project")} <ArrowRight size={14} />
          </Link>
          <a href={`tel:${phone}`} data-testid="final-call-btn" className="border border-[#1A1A1A]/30 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-10 py-5 hover:bg-[#1A1A1A] hover:text-[#F7F5F2] transition-colors duration-500">
            {phone}
          </a>
        </div>
      </FadeUp>
    </div>
  </section>
  );
};

const Home = () => (
  <main>
    <SEO />
    <Hero />
    <Intro />
    <FeaturedHome />
    <CustomPromo />
    <EditorialMarquee items={["Built With Intention", "Built For You", "Idaho Craftsmanship", "Custom Homes"]} />
    <PortfolioPreview />
    <WhyChoose />
    <ProcessTeaser />
    <Testimonials />
    <InstagramSection />
    <ServiceArea />
    <FinalCTA />
  </main>
);

export default Home;
